/**
 * useNetworkDetector.js
 *
 * A zero-dependency composable that replicates v-offline's VOffline component
 * logic exactly, with one addition: interval-based polling while online
 * to catch "connected to WiFi but no real internet" scenarios that the
 * browser never fires events for.
 *
 * ─── How v-offline works (source analysis) ───────────────────────────────────
 *
 * 1. Registers window listeners on ['online', 'offline', 'load'].
 * 2. On each event calls check():
 *      → uses ping.js, which loads an Image from `${url}/favicon.ico?_=<ts>`
 *      → ping.js RESOLVES when the image loads and REJECTS on an error or
 *        timeout
 * 3. Emits 'detected-condition' with a boolean:
 *      try { ping resolves → (ms > 0 || navigator.onLine) → emit true  }
 *      catch{ error thrown → (!navigator.onLine || error)  → emit false }
 *
 * ─── Our addition ────────────────────────────────────────────────────────────
 *
 * While isOnline is true we run the same check() on a POLL_INTERVAL.
 * This catches captive portals and WiFi adapters that report "connected"
 * but have no upstream internet — the browser never fires 'offline' for these.
 * On poll failure isOnline flips to false; on the next browser 'online' event
 * (or a successful poll once the user reconnects) it flips back.
 *
 * ─── Shared instances (collector-app P1-C) ───────────────────────────────────
 *
 * `createNetworkDetector()` is the same machine WITHOUT component lifecycle,
 * for a module-level instance several screens share (the collector's sync
 * pill: ONE detector pointed at our API, see src/collector/network.js). It
 * adds three options, all off by default so `useNetworkDetector()` behaves
 * exactly as before:
 *   - `probe`: replaces the ping.js image load (a fetch to our own server, for
 *     "online" meaning "our server answers");
 *   - `offlinePollIntervalMs`: keep probing while offline (a phone in a market
 *     with bars but no data never fires the browser's 'online' event);
 *   - `report(online)`: lets a real request's outcome set the state — any HTTP
 *     answer is online, a network error or timeout is offline.
 * `start()` / `stop()` are refcounted: the listeners and the poll run while at
 * least one screen holds the detector.
 *
 * ─── Usage ───────────────────────────────────────────────────────────────────
 *
 *   const { isOnline } = useNetworkDetector();
 *   watch(isOnline, (online) => { ... });
 */
import Ping from 'ping.js';
import { onBeforeUnmount, onMounted, ref } from 'vue';

// Mirror v-offline's default
const DEFAULT_PING_URL = 'https://google.com';

// How often to re-verify connectivity while online.
// 30 s is a good production balance: fast enough to catch captive portals,
// slow enough to not generate noise.
const POLL_INTERVAL_MS = 30_000;

// v-offline listens to exactly these three events
const EVENTS = ['online', 'offline', 'load'];

/**
 * @param {string} [pingUrl]
 * @param {{
 *   probe?: () => Promise<boolean>,
 *   pollIntervalMs?: number,
 *   offlinePollIntervalMs?: number|null,
 *   timeoutMs?: number,
 * }} [options]
 */
export function createNetworkDetector(
  pingUrl = DEFAULT_PING_URL,
  { probe = null, pollIntervalMs = POLL_INTERVAL_MS, offlinePollIntervalMs = null, timeoutMs = 0 } = {},
) {
  // Initialise from navigator.onLine — same as v-offline
  const isOnline = ref(typeof navigator === 'undefined' ? true : navigator.onLine);

  let pollTimer = null;
  let holders = 0;

  // ─── check: replicates v-offline's check() exactly ────────────────────────
  //
  // Returns a boolean: true = online, false = offline.
  // Mirrors the original try/catch + navigator.onLine guards precisely.
  //
  async function check() {
    if (probe) {
      try {
        return (await probe()) === true;
      } catch {
        return false;
      }
    }
    try {
      const p = new Ping(timeoutMs ? { timeout: timeoutMs } : undefined);
      const ms = await p.ping(pingUrl);
      // v-offline: `if (ping || navigator.onLine)` — ping is the ms value (truthy)
      if (ms) {
        return true;
      }
    } catch {
      // v-offline: `if (error || !navigator.onLine)`
      return false;
    }
    // Fallback: trust the browser flag
    return navigator.onLine;
  }

  // ─── polling (our addition) ────────────────────────────────────────────────
  //
  // While online: re-verify every `pollIntervalMs` (captive portal / wifi drop).
  // While offline: only when `offlinePollIntervalMs` is set; otherwise wait
  // for the browser's 'online' event, as v-offline does.

  function stopPolling() {
    clearInterval(pollTimer);
    pollTimer = null;
  }

  function schedulePolling() {
    stopPolling();
    if (holders === 0) return;
    const interval = isOnline.value ? pollIntervalMs : offlinePollIntervalMs;
    if (!interval) return;
    pollTimer = setInterval(async () => {
      report(await check());
    }, interval);
  }

  /** Set the state from any evidence (a probe, or a real request's outcome). */
  function report(online) {
    const next = online === true;
    if (isOnline.value === next) return;
    isOnline.value = next;
    schedulePolling();
  }

  // ─── event handler (drives both state + polling lifecycle) ────────────────
  //
  // Called for every window event in EVENTS, same as v-offline.
  //
  async function handleEvent() {
    report(await check());
  }

  // ─── lifecycle ────────────────────────────────────────────────────────────

  function start() {
    holders += 1;
    if (holders > 1) return;
    EVENTS.forEach((e) => window.addEventListener(e, handleEvent));
    schedulePolling();
    handleEvent(); // initial state check (mirrors v-offline's 'load' listener)
  }

  function stop() {
    if (holders === 0) return;
    holders -= 1;
    if (holders > 0) return;
    EVENTS.forEach((e) => window.removeEventListener(e, handleEvent));
    stopPolling();
  }

  return { isOnline, check, report, start, stop };
}

export function useNetworkDetector(pingUrl = DEFAULT_PING_URL) {
  const detector = createNetworkDetector(pingUrl);

  // v-offline registers listeners in `created`.
  // We do it in onMounted + fire an explicit initial check because by the time
  // this composable mounts the 'load' event has already fired.
  onMounted(() => detector.start());
  onBeforeUnmount(() => detector.stop());

  return { isOnline: detector.isOnline };
}
